/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useMemo } from 'react';

import { SkillAttainmentProgress_AttainmentStatus as AttainmentStatus } from '__generated__/graphql-types';

import { Skeleton } from '@coursera/cds-core';

import { useSkillAreasThresholdRequestTracker } from 'bundles/assess-common/context/SkillAreasThresholdRequestTracker';
import SkillProgressBar from 'bundles/skills-common/shared/verified-skills/SkillProgressBar';
import SkillProgressBarWithThresholdStar from 'bundles/skills-common/shared/verified-skills/SkillProgressBarWithThresholdStar';
import { useSkillPathwayAttainmentsDataContext } from 'bundles/skills-common/shared/verified-skills/context/skill-pathway/SkillPathwayAttainmentsDataContext';
import type { SkillAttainmentSkillAreaAttainmentFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import { useGetSkillMasteryDetailsForSkillArea } from 'bundles/skills-common/shared/verified-skills/hooks/useGetSkillMasteryDetailsForSkillArea';
import { getSkillAttainmentForCurrentCourse } from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

type Props = {
  className?: string;
  total: number;
  score: number;
  title: string;
  id: string;
  courseId: string;
  useBoldText?: boolean;
  hasCompletedSkillPathway?: boolean;
};

const styles = {
  loader: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
  loaderHeader: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,
};

const SkillAreasProgressBarSkeleton = () => (
  <div css={styles.loader}>
    <div css={styles.loaderHeader}>
      <Skeleton variant="text" width={154} />
      <Skeleton variant="text" width={26} />
    </div>

    <Skeleton variant="rect" height={4} />
  </div>
);

const SkillAreasProgressBarWithThresholdMain = ({
  courseId,
  skillAttainmentForCourse,
  hasCompletedSkillPathway,
  ...props
}: Props & { skillAttainmentForCourse?: SkillAttainmentSkillAreaAttainmentFragment }) => {
  const { showProgressBars, registerLoader, setLoaderComplete } = useSkillAreasThresholdRequestTracker();

  const { skillAttainmentThreshold, loading, error } = useGetSkillMasteryDetailsForSkillArea({
    skillAttainmentForCourse,
    courseId,
  });

  useEffect(() => {
    registerLoader(props.id);
  }, [registerLoader, props.id]);

  useEffect(() => {
    if (!loading && !error) {
      setLoaderComplete(props.id);
    }
  }, [loading, error, setLoaderComplete, props.id]);

  if (!showProgressBars) {
    return <SkillAreasProgressBarSkeleton />;
  }

  if (error) {
    return <SkillProgressBar {...props} />;
  }

  return (
    <SkillProgressBarWithThresholdStar
      {...props}
      threshold={skillAttainmentThreshold}
      useTransparentThresholdBorderColor={hasCompletedSkillPathway}
    />
  );
};

const SkillAreasProgressBarWithThreshold = (props: Props) => {
  const { id, courseId, hasCompletedSkillPathway } = props;
  const { showProgressBars, registerLoader, setLoaderComplete } = useSkillAreasThresholdRequestTracker();
  const { skillAttainments, loading: loadingSkillAttainments } = useSkillPathwayAttainmentsDataContext();

  const skillAttainmentForCourse = getSkillAttainmentForCurrentCourse(skillAttainments || [], courseId);

  const hasMasteredNoSkills = useMemo(
    () =>
      !skillAttainmentForCourse?.skillExpressionsAttainment?.some(
        (expression) => expression.attainmentStatus === AttainmentStatus.FullAttainment
      ),
    [skillAttainmentForCourse]
  );

  const shouldShowFallback = hasMasteredNoSkills || loadingSkillAttainments;

  useEffect(() => {
    if (shouldShowFallback) {
      registerLoader(id);
    }
  }, [shouldShowFallback, registerLoader, id]);

  useEffect(() => {
    if (shouldShowFallback && !loadingSkillAttainments) {
      setLoaderComplete(id);
    }
  }, [shouldShowFallback, loadingSkillAttainments, setLoaderComplete, id]);

  if (shouldShowFallback) {
    if (!showProgressBars) {
      return <SkillAreasProgressBarSkeleton />;
    }
    return (
      <SkillProgressBarWithThresholdStar {...props} useTransparentThresholdBorderColor={hasCompletedSkillPathway} />
    );
  }

  return <SkillAreasProgressBarWithThresholdMain {...props} skillAttainmentForCourse={skillAttainmentForCourse} />;
};

export default SkillAreasProgressBarWithThreshold;
